"use client";

import { useState, type FormEvent } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import {
  isValidProposalAccessCode,
  storeProposalAccessSession,
} from "../proposalAccess";
import {
  PROPOSAL_FONTS_HREF,
  proposalDisplay,
  proposalFont,
  proposalTokens,
} from "../proposalTheme";

export default function ProposalPassphraseGate({
  slug,
  clientName,
  accessEnvKey,
  onVerified,
  jetsTheme = false,
}: {
  slug: string;
  clientName: string;
  accessEnvKey: string;
  onVerified: () => void;
  jetsTheme?: boolean;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    if (!isValidProposalAccessCode(accessEnvKey, code)) {
      setSubmitting(false);
      setError("That password is not correct.");
      return;
    }
    storeProposalAccessSession(slug);
    setSubmitting(false);
    onVerified();
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: jetsTheme ? "#125740" : proposalTokens.bg,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2.5,
        py: 8,
      }}
    >
      <Helmet>
        <title>{clientName} Proposal | ComedyUO</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href={PROPOSAL_FONTS_HREF} />
      </Helmet>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, md: 4 },
          borderRadius: "4px",
          border: `1px solid ${jetsTheme ? "rgba(255,255,255,0.28)" : proposalTokens.border}`,
          bgcolor: jetsTheme ? "#0C3D2C" : proposalTokens.bgElevated,
        }}
      >
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: jetsTheme ? "#FFFFFF" : proposalTokens.accent,
            mb: 1.5,
          }}
        >
          Private proposal
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: proposalDisplay,
            fontWeight: 500,
            color: proposalTokens.text,
            fontSize: { xs: "1.75rem", md: "2rem" },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            mb: 1.25,
          }}
        >
          Enter the password
        </Typography>
        <Typography
          sx={{
            fontFamily: proposalFont,
            color: proposalTokens.textMuted,
            fontSize: "0.95rem",
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          This {clientName} proposal is private. Enter the password ComedyUO sent you.
        </Typography>
        <TextField
          fullWidth
          required
          autoComplete="off"
          type="password"
          label="Password"
          placeholder="Password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              color: proposalTokens.text,
              bgcolor: "rgba(255,255,255,0.04)",
              alignItems: "center",
              minHeight: 56,
            },
            "& .MuiInputBase-input": {
              py: 1.75,
              lineHeight: 1.4,
              letterSpacing: "0.06em",
            },
            "& .MuiInputLabel-root": { color: proposalTokens.textFaint },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: proposalTokens.border,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${jetsTheme ? "#FFFFFF" : proposalTokens.accent} !important`,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: jetsTheme ? "#FFFFFF" : proposalTokens.accent,
            },
          }}
        />
        {error ? (
          <Typography
            sx={{
              fontFamily: proposalFont,
              color: "#f87171",
              fontSize: "0.875rem",
              mb: 2,
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          type="submit"
          disabled={submitting || !code.trim()}
          fullWidth
          sx={{
            fontFamily: proposalFont,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: jetsTheme ? "#FFFFFF" : proposalTokens.accent,
            color: jetsTheme ? "#125740" : "#0c0b0a",
            borderRadius: "4px",
            py: 1.4,
            fontSize: "0.975rem",
            "&:hover": {
              bgcolor: jetsTheme ? "#FFFFFF" : proposalTokens.accent,
              filter: "brightness(1.08)",
            },
            "&.Mui-disabled": { color: jetsTheme ? "rgba(18,87,64,0.45)" : "rgba(12,11,10,0.45)" },
          }}
        >
          {submitting ? (
            <CircularProgress size={22} sx={{ color: jetsTheme ? "#125740" : "#0c0b0a" }} />
          ) : (
            "Continue"
          )}
        </Button>
      </Box>
    </Box>
  );
}
